// Shared, framework-agnostic formatter for the client-side accept-invitation telemetry lines.
//
// The lines intentionally mirror the server-side scheme emitted by AdsAppUI's
// `ActivateMsaUserController` (`InvitationIssues::Result step=… outcome=… errorCode=…`) so the
// client report can be built with the SAME parsing the server report already uses (and the two
// can even be unioned). Every line additionally carries `code=<invitationCode>` so a report can
// be keyed on either the automatic telemetry session id (`SesID`) OR the invitation code — the
// choice of denominator (per-session vs per-invitation) is deferred to query time.
//
// This module is pure string building (no React, no logger) so BOTH the pre-auth landing page
// (idp-selection-page) and the post-auth page (accept-invitation-page) emit an identical schema.

// The stable prefix the report filters on — matches the server-side raw-log prefix.
export const INVITATION_LOG_PREFIX = 'InvitationIssues::Result';

// The flow step the line belongs to. `PreAuth` = the pre-auth IDP-selection landing page;
// `PostAuth` = the post-auth accept-invitation page; `Activate` = the Activate API result;
// `Confirmation` = the "You're all set" page. (The server uses `Index`/`Activate`; the client
// uses distinct step names so a client-only report is unambiguous while still union-able.)
export type InvitationLogStep = 'PreAuth' | 'PostAuth' | 'Activate' | 'Confirmation';

export type InvitationLogFields = {
  step: InvitationLogStep;
  outcome: string;
  // A stable error token (e.g. a server error code, an HTTP status like `HTTP401`, or `None` on
  // success). Defaults to `None` when omitted.
  errorCode?: string;
  // The invitation code — added to every line so the report can dedupe per-invitation.
  code?: string;
  // Optional extra key=value context (e.g. { idP: 'AAD', customerId: 12345 }). Null/undefined
  // values are skipped.
  extra?: Record<string, string | number | boolean | null | undefined>;
};

// Builds a single telemetry line, e.g.
//   InvitationIssues::Result step=Activate outcome=Success errorCode=None code=abc123 customerId=12345 idP=AAD
//
// Dynamic values (outcome, errorCode, code, and every extra value) are URL-encoded before being
// appended. The line is space-delimited `key=value`, and the report parser splits on whitespace
// (e.g. `extract(@"code=([^\s]+)", …)`); a value containing a space — a URL-supplied invitation
// code such as `abc outcome=Success` is the realistic case — would otherwise inject extra tokens
// and break downstream parsing. `step` is an internal enum (never dynamic) so it is left as-is.
// Encoding is a no-op for the common alphanumeric values, so existing lines are unchanged.
export const buildInvitationLog = ({
  step, outcome, errorCode = 'None', code, extra,
}: InvitationLogFields): string => {
  const parts = [
    INVITATION_LOG_PREFIX,
    `step=${step}`,
    `outcome=${encodeURIComponent(outcome)}`,
    `errorCode=${encodeURIComponent(errorCode)}`,
  ];

  if (code) {
    parts.push(`code=${encodeURIComponent(code)}`);
  }

  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        parts.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    });
  }

  return parts.join(' ');
};
